/**
 * features/finance/FinancialYear.tsx
 * ------------------------------------------------------------------
 * CLOSING THE MONTH — the control that existed and could not be used.
 *
 * The posting engine has refused to write into a closed period since the
 * day it was written; glWrite and poWrite both check before every entry.
 * Nothing in the app could close one. The lock was enforced and
 * unreachable, which is a control that exists on paper and nowhere else.
 *
 * WHAT CLOSING IS FOR. The moment a figure leaves the building — a board
 * pack, a management account, statements to the auditor — anything
 * posted into that month afterwards makes the copy in somebody's hand
 * wrong, silently. Closing is the school saying "this month is now what
 * it is".
 *
 * MONTHS CLOSE IN ORDER AND REOPEN IN REVERSE, and the screen refuses
 * rather than warns. Both rules exist for one reason: an opening position
 * that quietly stops matching the close before it.
 *
 * REOPENING IS ALLOWED, and recorded. Refusing it outright would
 * guarantee that the first genuine correction gets made by editing
 * something the lock cannot see.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import {
  CalendarCheck, Lock, LockOpen, AlertTriangle, CheckCircle2, Info,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { db, setDoc } from '@/lib/firebase';
import { GL_COLLECTIONS, type GlPeriod } from '@/lib/gl';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import {
  FY_DEFAULT, MONTH_NAMES, buildPeriodRows, closeProblem, reopenProblem,
  fyProgress, periodsInFy,
  type FySettings, type PeriodRow,
} from '@/lib/financialYear';
import { useGlData } from '@/features/finance/useGlData';
import { closePeriod, reopenPeriod } from '@/features/finance/glWrite';
import { buildTrialBalance } from '@/lib/glReport';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function FinancialYear() {
  const { userData } = useAuth();
  const thisYear = new Date().getFullYear();
  const [fy, setFy] = useState(`FY${thisYear}`);

  const [periods, setPeriods] = useState<GlPeriod[] | null>(null);
  const [settings, setSettings] = useState<FySettings>(FY_DEFAULT);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const gl = useGlData(fy);

  useEffect(() => onSnapshot(
    query(collection(db, GL_COLLECTIONS.periods)),
    (s) => setPeriods(s.docs.map((d) => ({ id: d.id, ...d.data() }) as GlPeriod)),
    () => setPeriods([]),
  ), []);

  useEffect(() => onSnapshot(
    doc(db, FINANCE_COLLECTIONS.settings, 'school'),
    (s) => setSettings((s.data()?.financialYear ?? FY_DEFAULT) as FySettings),
    () => setSettings(FY_DEFAULT),
  ), []);

  const rows = useMemo(
    () => buildPeriodRows(fy, periods ?? [], settings),
    [fy, periods, settings],
  );
  const progress = useMemo(() => fyProgress(rows), [rows]);

  /** What each month actually carries, so nobody closes an empty one by
      accident or a busy one without looking. */
  const activity = useMemo(() => {
    const by = new Map<string, number>();
    for (const j of gl.journals) {
      const p = (j.date ?? '').slice(0, 7);
      by.set(p, (by.get(p) ?? 0) + 1);
    }
    return by;
  }, [gl.journals]);

  async function close(row: PeriodRow) {
    const problem = closeProblem(row);
    if (problem) { setErr(problem); return; }
    const note = window.prompt(
      `Close ${row.label}? Nothing can be posted into it afterwards until it is reopened.\n\n`
      + 'Anything worth recording about the close (optional):',
    );
    if (note === null) return;

    setBusy(row.id); setErr(null);
    try {
      await closePeriod(
        row.id, row.fy,
        { uid: userData?.uid, name: userData?.displayName },
        note.trim() || undefined,
      );
    } catch (e) {
      setErr((e as Error).message);
    }
    setBusy(null);
  }

  async function reopen(row: PeriodRow) {
    const problem = reopenProblem(row, rows);
    if (problem) { setErr(problem); return; }
    const why = window.prompt(
      `Reopen ${row.label}? Anything already reported for that month may change.\n\n`
      + 'Why is it being reopened?',
    );
    if (!why?.trim()) return;

    setBusy(row.id); setErr(null);
    try {
      /* reopenPeriod DELETES the row, because absent means open and a
         second way to say the same thing is a way for the two to
         disagree. The reason is recorded as an audit entry rather than on
         a document that is about to stop existing. */
      await reopenPeriod(row.id);
      await setDoc(doc(db, 'reportAuditLog', `period-reopen-${row.id}-${Date.now()}`), {
        action: 'period.reopen',
        period: row.id,
        reason: why.trim(),
        by: userData?.uid ?? '',
        byName: userData?.displayName ?? '',
        at: new Date().toISOString(),
      });
    } catch (e) {
      setErr((e as Error).message);
    }
    setBusy(null);
  }

  async function saveSettings(next: FySettings) {
    setSettings(next);
    await setDoc(
      doc(db, FINANCE_COLLECTIONS.settings, 'school'),
      { financialYear: next, updatedAt: new Date().toISOString() },
      { merge: true },
    );
  }

  if (periods === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const tb = buildTrialBalance(gl.accounts, gl.journals, {
    from: periodsInFy(fy, settings)[0] + '-01',
    to: `${periodsInFy(fy, settings)[11]}-31`,
  });

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        icon={CalendarCheck} tone="navy"
        title="Financial Year"
        subtitle="Closing a month is the school saying it is now what it is"
        actions={(
          <select value={fy} onChange={(e) => setFy(e.target.value)}
            className={glassFieldClass('max-w-[130px] text-sm')}>
            {[1, 0, -1].map((n) => {
              const y = thisYear - n;
              return <option key={y} value={`FY${y}`}>{`FY${y}`}</option>;
            })}
          </select>
        )}
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />{err}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" icon={Lock}
          value={`${progress.closed} of ${progress.total}`} label="Months closed" />
        <StatChip tone={progress.upToDate ? 'emerald' : 'amber'}
          icon={progress.upToDate ? CheckCircle2 : undefined}
          value={progress.upToDate ? 'Up to date' : (progress.nextToClose?.label ?? '—')}
          label={progress.upToDate ? 'Nothing waiting' : 'Next to close'} />
        <StatChip tone={tb.balanced ? 'slate' : 'rose'}
          value={tb.balanced ? 'Balances' : money(tb.difference)} label="Trial balance for the year" />
      </div>

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader icon={CalendarCheck} tone="navy" title="When the year runs"
          subtitle="Stored, not assumed — a school on an April year end should not find out at year end" />
        <div className="p-4 flex flex-wrap gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Year starts in
            </span>
            <select value={settings.startMonth}
              onChange={(e) => saveSettings({ ...settings, startMonth: Number(e.target.value) })}
              className={glassFieldClass('mt-1')}>
              {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              A year is named by
            </span>
            <select value={settings.labelBy}
              onChange={(e) => saveSettings({ ...settings, labelBy: e.target.value as 'start' | 'end' })}
              className={glassFieldClass('mt-1')}>
              <option value="start">the year it starts in</option>
              <option value="end">the year it ends in</option>
            </select>
          </label>
        </div>
      </SoftCard>

      <SoftCard pad="p-0">
        <PanelHeader icon={Lock} tone="navy" title="The months"
          subtitle="Close in order; reopen in reverse" />

        <div className="divide-y divide-gray-50 dark:divide-gray-800">
          {rows.map((r) => {
            const entries = activity.get(r.id) ?? 0;
            const cantClose = closeProblem(r);
            const cantReopen = reopenProblem(r, rows);
            return (
              <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[180px]">
                  <span className="font-medium text-gray-900 dark:text-white">{r.label}</span>
                  <span className="block text-[12px] text-gray-500">
                    {entries} journal{entries === 1 ? '' : 's'}
                    {r.closed && r.closedAt && ` · closed ${r.closedAt.slice(0, 10)}`}
                    {r.closed && r.closedByName && ` by ${r.closedByName}`}
                  </span>
                  {r.note && (
                    <span className="block text-[12px] text-gray-400 italic">{r.note}</span>
                  )}
                  {/* The reason it cannot be done, in place, rather than a
                      button that is simply absent. */}
                  {!r.closed && cantClose && !r.future && (
                    <span className="block text-[12px] text-amber-700 dark:text-amber-300 mt-0.5">
                      {cantClose}
                    </span>
                  )}
                </div>

                <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                  r.closed
                    ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                    : r.future
                      ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                }`}>
                  {r.closed ? 'Closed' : r.future ? 'Not yet' : 'Open'}
                </span>

                {r.closed ? (
                  <button type="button" className={btnGhost} disabled={busy === r.id || Boolean(cantReopen)}
                    title={cantReopen ?? 'Reopen this month'}
                    onClick={() => reopen(r)}>
                    <LockOpen size={14} /> Reopen
                  </button>
                ) : (
                  <button type="button" className={btnPrimary}
                    disabled={busy === r.id || Boolean(cantClose)}
                    title={cantClose ?? 'Close this month'}
                    onClick={() => close(r)}>
                    <Lock size={14} /> Close
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-gray-500 flex items-start gap-1.5">
          <Info size={13} className="mt-0.5 shrink-0" />
          A month with no entry here is OPEN. Only closed months are recorded, so a school
          that has never closed anything can always post. Year-end — emptying income and
          expenditure into the accumulated fund — is on the Period End screen.
        </p>
      </SoftCard>
    </div>
  );
}
